import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Plus,
  Search,
  Workflow,
  Edit,
  Trash2,
  Copy,
  Clock,
  FileJson,
  Send,
  Loader2,
  AlertTriangle,
  X,
  CheckCircle,
  XCircle,
  Eye,
  Ban,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-toastify";
import MetaFlowBuilder from "../components/flows/MetaFlowBuilder";
import { createStarterFlow, buildFlowJSON, uid } from "../utils/flowJsonBuilder";
import { FLOW_CATEGORIES } from "../utils/flowConstants";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import {
  fetchWaFlows,
  getWaFlow,
  saveWaFlow,
  deleteWaFlow,
  previewWaFlow,
  deprecateWaFlow,
} from "../apis/whatsappFlows";

const STATUS_CHIP = {
  draft: { label: "Draft", tone: "n" },
  published: { label: "Published", tone: "g" },
  failed: { label: "Failed", tone: "r" },
  pending_publish: { label: "Pending", tone: "a" },
  deprecated: { label: "Deprecated", tone: "n" },
};

const WhatsAppFlows = () => {
  const { selectedPhoneId, selectedPhoneAccount } = usePhoneAccount();
  const wabaId = selectedPhoneAccount?.wabaId || null;
  const phoneId = selectedPhoneId || null;

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFlow, setEditingFlow] = useState(null);
  const [mode, setMode] = useState("list");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const latestFlowJsonRef = useRef(null);

  useEffect(() => {
    if (mode !== "builder") {
      document.documentElement.removeAttribute("data-ping-collapse-nav");
      return undefined;
    }
    document.documentElement.setAttribute("data-ping-collapse-nav", "");
    return () => document.documentElement.removeAttribute("data-ping-collapse-nav");
  }, [mode]);

  // Load flows from backend
  const loadFlows = useCallback(async () => {
    if (!phoneId) return;
    setLoading(true);
    try {
      const res = await fetchWaFlows({ phoneId });
      if (res.success) setFlows(res.flows || []);
    } catch (err) {
      console.error("Failed to load flows:", err);
    } finally {
      setLoading(false);
    }
  }, [phoneId]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const createNewFlow = useCallback(() => {
    const flow = createStarterFlow();
    setEditingFlow({
      _isNew: true,
      id: null,
      name: "New Flow",
      categories: ["LEAD_GENERATION"],
      flow_json: flow,
      meta_status: "draft",
    });
    setSubmitError(null);
    setMode("builder");
  }, []);

  const editFlow = useCallback(async (flowEntry) => {
    try {
      const res = await getWaFlow(flowEntry.id);
      if (res.success && res.flow) {
        const addUid = (obj) => {
          if (Array.isArray(obj)) return obj.map(addUid);
          if (obj && typeof obj === "object") {
            const out = { ...obj, _uid: uid() };
            Object.keys(out).forEach((k) => {
              if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
            });
            return out;
          }
          return obj;
        };
        setEditingFlow({
          ...res.flow,
          flow_json: addUid(res.flow.flow_json),
        });
        setSubmitError(null);
        setMode("builder");
      }
    } catch (err) {
      toast.error("Failed to load flow for editing");
    }
  }, []);

  const handleSave = useCallback(
    async (publish = false) => {
      if (!editingFlow) return;
      if (!wabaId || !phoneId) {
        toast.error("Please select a WhatsApp account first");
        return;
      }

      const flowState = latestFlowJsonRef.current || editingFlow.flow_json;
      const sanitizedJson = buildFlowJSON(flowState);

      const setter = publish ? setPublishing : setSaving;
      setter(true);
      setSubmitError(null);

      try {
        const payload = {
          id: editingFlow._isNew ? undefined : editingFlow.id,
          name: editingFlow.name,
          categories: editingFlow.categories,
          flow_json: sanitizedJson,
          wabaId,
          phoneId,
          publish,
        };

        const res = await saveWaFlow(payload);

        if (res.success) {
          toast.success(publish ? "Flow published to WhatsApp" : "Flow saved");
          setMode("list");
          setEditingFlow(null);
          loadFlows();
        }
      } catch (err) {
        const errData = err.response?.data || {};
        const raw = errData.rawError || {};
        const primaryMsg = raw.error_user_msg || errData.error || errData.details || err.message || "Operation failed";
        const errorObj = {
          message: primaryMsg,
          title: raw.error_user_title || errData.errorTitle || null,
          step: errData.step || null,
          validationErrors: errData.validationErrors || null,
          rawError: raw,
        };
        setSubmitError(errorObj);

        if (errData.flow) {
          setEditingFlow((prev) => ({
            ...prev,
            ...errData.flow,
            _isNew: false,
            flow_json: prev.flow_json,
          }));
        }
      } finally {
        setter(false);
      }
    },
    [editingFlow, wabaId, phoneId, loadFlows]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this flow?")) return;
      try {
        await deleteWaFlow(id);
        setFlows((prev) => prev.filter((f) => f.id !== id));
        toast.success("Flow deleted");
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to delete flow");
      }
    },
    []
  );

  const handlePreview = useCallback(async (id) => {
    try {
      const res = await previewWaFlow(id);
      const url = res.preview?.preview_url || res.preview?.url;
      if (!url) {
        toast.error("No preview URL returned");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to open preview");
    }
  }, []);

  const handleDeprecate = useCallback(async (id) => {
    if (!window.confirm("Deprecate this published flow? It cannot be used in new messages after this.")) {
      return;
    }
    try {
      await deprecateWaFlow(id);
      toast.success("Flow deprecated");
      loadFlows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to deprecate flow");
    }
  }, [loadFlows]);

  const duplicateFlow = useCallback(
    async (flowEntry) => {
      try {
        const res = await getWaFlow(flowEntry.id);
        if (!res.success) return;
        const addUid = (obj) => {
          if (Array.isArray(obj)) return obj.map(addUid);
          if (obj && typeof obj === "object") {
            const out = { ...obj, _uid: uid() };
            Object.keys(out).forEach((k) => {
              if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
            });
            return out;
          }
          return obj;
        };
        setEditingFlow({
          _isNew: true,
          id: null,
          name: `${flowEntry.name} (Copy)`,
          categories: res.flow.categories,
          flow_json: addUid(res.flow.flow_json),
          meta_status: "draft",
        });
        setSubmitError(null);
        setMode("builder");
      } catch {
        toast.error("Failed to duplicate flow");
      }
    },
    []
  );

  const importFlowJson = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (!parsed.version || !parsed.screens) return;
          const addUid = (obj) => {
            if (Array.isArray(obj)) return obj.map(addUid);
            if (obj && typeof obj === "object") {
              const out = { ...obj, _uid: uid() };
              Object.keys(out).forEach((k) => {
                if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
              });
              return out;
            }
            return obj;
          };
          setEditingFlow({
            _isNew: true,
            id: null,
            name: file.name.replace(".json", ""),
            categories: ["OTHER"],
            flow_json: addUid(parsed),
            meta_status: "draft",
          });
          setSubmitError(null);
          setMode("builder");
        } catch {
          toast.error("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // ────── Builder mode ──────

  if (mode === "builder" && editingFlow) {
    const backToList = () => {
      setMode("list");
      setEditingFlow(null);
      setSubmitError(null);
    };

    return (
      <div className="waf-builder">
        <div className="fb-bar">
          <button
            type="button"
            className="camp-ico"
            onClick={backToList}
            title="Back to flows"
            aria-label="Back to flows"
          >
            <ArrowLeft size={16} />
          </button>
          <input
            value={editingFlow.name}
            onChange={(e) => setEditingFlow((prev) => ({ ...prev, name: e.target.value }))}
            className="fb-name"
            placeholder="Flow name"
          />
          <select
            value={editingFlow.categories?.[0] || "LEAD_GENERATION"}
            onChange={(e) => setEditingFlow((prev) => ({ ...prev, categories: [e.target.value] }))}
            className="waf-cat"
          >
            {FLOW_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <span className={`ping-chip ${STATUS_CHIP[editingFlow.meta_status]?.tone || "n"}`}>
            {STATUS_CHIP[editingFlow.meta_status]?.label || "Draft"}
          </span>
          <div className="grow" />
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving || publishing}
            className="ping-btn ping-btn-ghost ping-btn-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Save draft
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saving || publishing}
            className="ping-btn ping-btn-green ping-btn-sm"
          >
            {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Publish
          </button>
        </div>

        {submitError && (
          <div className="fb-val">
            <div className="ping-banner red">
              <AlertTriangle size={16} />
              <div className="min-w-0 flex-1">
                {submitError.title && <b>{submitError.title}</b>}
                <p>{submitError.message}</p>
                {submitError.validationErrors?.length > 0 && (
                  <div className="waf-err-list">
                    {submitError.validationErrors.map((ve, i) => (
                      <code key={i}>
                        {ve.message || ve.error || JSON.stringify(ve)}
                        {ve.line_start ? ` (line ${ve.line_start})` : ""}
                      </code>
                    ))}
                  </div>
                )}
                {submitError.rawError && Object.keys(submitError.rawError).length > 0 && (
                  <details>
                    <summary>Technical details</summary>
                    <pre>{JSON.stringify(submitError.rawError, null, 2)}</pre>
                  </details>
                )}
              </div>
              <button type="button" className="camp-ico" onClick={() => setSubmitError(null)} aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        <MetaFlowBuilder
          initialFlow={editingFlow.flow_json}
          onChange={(flowState) => {
            latestFlowJsonRef.current = flowState;
          }}
          onSave={(flowState) => {
            latestFlowJsonRef.current = flowState;
            handleSave(false);
          }}
          onBack={backToList}
        />
      </div>
    );
  }

  // ────── List mode ──────

  const filtered = flows.filter((f) => {
    const matchesSearch = f.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || f.meta_status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const publishedCount = flows.filter((f) => f.meta_status === "published").length;
  const draftCount = flows.filter((f) => f.meta_status === "draft").length;

  return (
    <div className="waf-page fl-page">
      <div className="page-head">
        <div>
          <h2>WhatsApp Flows</h2>
          <p>Create multi-screen forms for WhatsApp Business</p>
        </div>
        <div className="grow" />
        <button type="button" onClick={importFlowJson} className="ping-btn ping-btn-ghost ping-btn-sm">
          <FileJson size={16} />
          Import JSON
        </button>
        <button
          type="button"
          onClick={createNewFlow}
          disabled={!phoneId}
          className="ping-btn ping-btn-green ping-btn-sm"
        >
          <Plus size={16} />
          Create flow
        </button>
      </div>

      <div className="fl-kpis">
        <div className="fl-kpi hi">
          <div>
            <div className="k">Total flows</div>
            <div className="v">{flows.length}</div>
          </div>
          <div className="ico"><Workflow size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Published</div>
            <div className="v">{publishedCount}</div>
          </div>
          <div className="ico"><CheckCircle size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Drafts</div>
            <div className="v">{draftCount}</div>
          </div>
          <div className="ico"><FileJson size={14} /></div>
        </div>
      </div>

      <div className="fl-toolbar">
        <div className="fl-search">
          <Search size={15} />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search flows…"
          />
        </div>
        <div className="fl-filters">
          {[
            { id: "all", label: "All" },
            { id: "published", label: "Published" },
            { id: "draft", label: "Draft" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              className={`ping-ptab ${statusFilter === tab.id ? "on" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="fl-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="fl-card fl-skel">
              <div className="fl-skel-line" />
              <div className="fl-skel-line short" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="fl-empty">
          <div className="fl-empty-ico">
            <Workflow size={22} />
          </div>
          <h3>{flows.length === 0 ? "No flows yet" : "No matching flows"}</h3>
          <p>
            {flows.length === 0
              ? "Create your first WhatsApp Flow to get started."
              : "Try a different search or filter."}
          </p>
          {flows.length === 0 && (
            <button
              type="button"
              onClick={createNewFlow}
              disabled={!phoneId}
              className="ping-btn ping-btn-green ping-btn-sm"
            >
              <Plus size={16} />
              Create flow
            </button>
          )}
        </div>
      ) : (
        <div className="fl-grid">
          {filtered.map((flowEntry) => {
            const categoryLabel =
              FLOW_CATEGORIES.find((c) => c.value === flowEntry.categories?.[0])?.label ||
              flowEntry.categories?.[0];
            const badge = STATUS_CHIP[flowEntry.meta_status] || STATUS_CHIP.draft;

            return (
              <article
                key={flowEntry.id}
                className="fl-card"
                onClick={() => editFlow(flowEntry)}
              >
                <div className="fl-card-top">
                  <h3>{flowEntry.name}</h3>
                  <span className={`ping-chip ${badge.tone}`}>{badge.label}</span>
                </div>
                <div className="fl-card-chips">
                  {categoryLabel && <span className="ping-chip b">{categoryLabel}</span>}
                  {flowEntry.meta_status === "published" && (
                    <span className="ping-chip g">On WhatsApp</span>
                  )}
                </div>
                {flowEntry.meta_status === "failed" && flowEntry.last_meta_error && (
                  <p className="fl-card-desc waf-err" title={flowEntry.last_meta_error}>
                    <XCircle size={12} /> {flowEntry.last_meta_error}
                  </p>
                )}
                <div className="fl-card-foot">
                  <span className="fl-date">
                    <Clock size={12} />
                    {new Date(flowEntry.updated_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <div className="fl-actions">
                    {flowEntry.meta_flow_id && (
                      <button
                        type="button"
                        className="camp-ico"
                        data-tip="Preview"
                        aria-label="Preview"
                        onClick={(e) => { e.stopPropagation(); handlePreview(flowEntry.id); }}
                      >
                        <Eye size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="camp-ico"
                      data-tip="Edit"
                      aria-label="Edit"
                      onClick={(e) => { e.stopPropagation(); editFlow(flowEntry); }}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      type="button"
                      className="camp-ico"
                      data-tip="Duplicate"
                      aria-label="Duplicate"
                      onClick={(e) => { e.stopPropagation(); duplicateFlow(flowEntry); }}
                    >
                      <Copy size={14} />
                    </button>
                    {flowEntry.meta_status === "published" && (
                      <button
                        type="button"
                        className="camp-ico"
                        data-tip="Deprecate"
                        aria-label="Deprecate"
                        onClick={(e) => { e.stopPropagation(); handleDeprecate(flowEntry.id); }}
                      >
                        <Ban size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="camp-ico danger"
                      data-tip="Delete"
                      aria-label="Delete"
                      onClick={(e) => { e.stopPropagation(); handleDelete(flowEntry.id); }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WhatsAppFlows;
